import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Flag, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEvents, useUpcomingEvents } from "@/hooks/useEvents";
import type { Event } from "@/types";

const formatEventType = (type: string) =>
  type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export default function HomeEventsSection() {
  const { t, i18n } = useTranslation("pages");

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const { data: upcomingData, isLoading: upcomingLoading } =
    useUpcomingEvents(3);
  const hasUpcoming = (upcomingData?.events?.length ?? 0) > 0;

  // Stable "now" so the query key never changes between renders (avoids 429s).
  const nowIso = useMemo(() => new Date().toISOString(), []);

  // Only fetch past events when we know there are no upcoming ones
  const { data: pastData, isLoading: pastLoading } = useEvents(
    { startDateTo: nowIso, limit: 3 },
    !upcomingLoading && !hasUpcoming,
  );

  const eventsLoading = upcomingLoading || (!hasUpcoming && pastLoading);
  const displayEvents = hasUpcoming
    ? (upcomingData?.events ?? [])
    : (pastData?.events ?? []);
  const sectionTitle = hasUpcoming
    ? t("home.upcomingEvents")
    : t("home.recentEvents");

  return (
    <section className="border-t border-gray-800 bg-gray-950 text-white">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400">
              {t("home.eventsLabel")}
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">
              {sectionTitle}
            </h2>
          </div>
          <Link
            to="/events"
            className="hidden items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-white sm:inline-flex"
          >
            {t("home.viewAll")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {eventsLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-800" />
            ))}
          </div>
        ) : !displayEvents.length ? (
          <div className="py-14 text-center text-gray-500">
            <Calendar className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">{t("home.noEventsYet")}</p>
            <p className="mt-1 text-xs">
              {t("home.beFirstToAddOne")}{" "}
              <Link to="/events" className="underline hover:text-gray-300">
                {t("home.addOne")}
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-800 border-t border-gray-800">
            {displayEvents.map((event: Event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="group -mx-4 flex flex-col justify-between gap-2 rounded-xl px-4 py-4 transition-colors hover:bg-gray-900 sm:flex-row sm:items-center"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold">{event.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      {event.track.name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Flag className="h-3.5 w-3.5 flex-shrink-0" />
                      {formatEventType(event.eventType)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <span className="whitespace-nowrap font-mono text-xs text-gray-400">
                    {formatDate(event.startDate)}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-600 transition-colors group-hover:text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-6 sm:hidden">
          <Link
            to="/events"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-white"
          >
            {t("home.viewAllEvents")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
